import React, { useMemo } from 'react';
import { Users } from 'lucide-react';

const TeamResponses = ({ responses, featureRef, organization }) => {
  // Filter and group responses by organization and response type
  const groupedResponses = useMemo(() => {
    // First filter responses for this feature and organization
    const featureResponses = responses.filter(r => 
        r.feature_reference === featureRef && 
        r.organization_id === organization?.id
    );

    // Get the most recent response for each user
    const latestResponses = Object.values(
        featureResponses.reduce((acc, response) => {
            const key = response.user_id;
            if (!acc[key] || new Date(response.updated_at) > new Date(acc[key].updated_at)) {
                acc[key] = response;
            }
            return acc;
        }, {})
    );

    // Then group by response type
    return {
        required: latestResponses.filter(r => r.response_value === 'required'),
        nice_to_have: latestResponses.filter(r => r.response_value === 'nice_to_have'),
        not_needed: latestResponses.filter(r => r.response_value === 'not_needed'),
        dont_know: latestResponses.filter(r => r.response_value === 'dont_know')
    };
}, [responses, featureRef, organization?.id]);

  // Helper function to get user initials
  const getInitials = (user) => {
    if (!user) return '';
    
    // Try to get initials from name first
    if (user.name) {
      const parts = user.name.split(/[\s-]+/).filter(part => part.length > 0);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].slice(0, 2).toUpperCase();
    }
    
    // Fallback to email
    if (user.email) {
      const beforeAt = user.email.split('@')[0];
      const parts = beforeAt.split(/[-_+\s.]/);
      const validParts = parts.filter(part => part.length > 0);
      return validParts.length >= 2 
        ? (validParts[0][0] + validParts[1][0]).toUpperCase()
        : validParts[0].slice(0, 2).toUpperCase();
    }
    
    return '??';
  };

  // Define response type configurations
  // In TeamResponses.jsx, update the responseTypes array
const responseTypes = [
  {
      type: 'required',
      title: 'Required',
      responses: groupedResponses.required,
      bgColor: 'bg-green-50',
      textColor: 'text-green-800',
      avatarBg: 'bg-green-100',
      borderColor: 'border-green-200'
  },
  {
      type: 'nice_to_have',
      title: 'Nice to Have',
      responses: groupedResponses.nice_to_have,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-800',
      avatarBg: 'bg-blue-100',
      borderColor: 'border-blue-200'
  },
  {
      type: 'not_needed',
      title: 'Not Needed',
      responses: groupedResponses.not_needed,
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-800',
      avatarBg: 'bg-gray-100',
      borderColor: 'border-gray-200'
  },
  // Add this new object
  {
      type: 'dont_know',
      title: 'Need Help Deciding',
      responses: groupedResponses.dont_know,
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-800',
      avatarBg: 'bg-amber-100',
      borderColor: 'border-amber-200'
  }
];

  // In TeamResponses.jsx
return (
  <div className="mt-2">
    <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
      <Users className="w-4 h-4" />
      <span>Team Responses</span>
    </div>
    
    {/* Update this grid to flex */}
    <div className="flex gap-4">
      {responseTypes.map(({ type, title, responses, bgColor, textColor, avatarBg, borderColor }) => (
        <div 
          key={type} 
          className={`flex-1 rounded-lg p-3 border ${borderColor} ${bgColor}`}
        >
          <h5 className={`text-sm font-medium mb-2 ${textColor}`}>
            {title}
          </h5>
          <div className="flex flex-wrap gap-1">
            {responses.length > 0 ? (
              responses.map(response => (
                <div 
                  key={response.user_id}
                  className="group relative"
                >
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${avatarBg} ${textColor}`}
                    title={response.user_name || response.email}
                  >
                    {getInitials({ name: response.user_name, email: response.email })}
                  </div>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                    {response.user_name || response.email}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 italic">
                No responses yet
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);
};

export default TeamResponses;